import { useEffect, useMemo, useState } from "react";
import { usePosts } from "../contexts/PostContext";
import { useUser } from "../contexts/UserContext";
import BoardList from "../components/BoardList";

import BoardWritePage from "./BoardWritePage";
import { Post } from "../types/board";
import { useNavigate } from "react-router-dom";
import { useNotices, Notice } from "../contexts/NoticeContext";
import { FREETALK_CATEGORIES } from "../constants/categories";

export default function BoardPage() {
  const { posts, loadPosts } = usePosts();
  const { user } = useUser();
  
  const [showWrite, setShowWrite] = useState(false);
  const navigate = useNavigate();
  const { notices } = useNotices();
  const pinnedNotices = notices.filter(n => n.is_pinned);
  const [selectedNotice, setSelectedNotice] = useState<Notice | null>(null);

  // 페이지네이션 & 검색 상태
  const PAGE_SIZE = 20;
  const [page, setPage] = useState(1);
  const [searchType, setSearchType] = useState<"title" | "title+content" | "author">("title");
  const [searchKeyword, setSearchKeyword] = useState("");

  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  useEffect(() => {
    loadPosts("Free-Talk");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    let filteredPosts = posts.filter(
      (p) => p.board_type === "Free-Talk" || p.board_type === "공동게시판"
    );

    if (selectedCategory !== "all") {
      filteredPosts = filteredPosts.filter((p: any) => p.category === selectedCategory);
    }

    if (searchKeyword.trim()) {
      const keyword = searchKeyword.trim().toLowerCase();
      if (searchType === "title") {
        filteredPosts = filteredPosts.filter(p => (p.title ?? "").toLowerCase().includes(keyword));
      } else if (searchType === "title+content") {
        filteredPosts = filteredPosts.filter(
          p =>
            (p.title ?? "").toLowerCase().includes(keyword) ||
            (p.content ?? "").toLowerCase().includes(keyword)
        );
      } else if (searchType === "author") {
        filteredPosts = filteredPosts.filter(p => (p.nickname ?? "").toLowerCase().includes(keyword));
      }
    }

    return filteredPosts;
  }, [posts, selectedCategory, searchType, searchKeyword]);

  useEffect(() => {
    setPage(1);
  }, [selectedCategory, searchType, searchKeyword]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pagedPosts = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function handleRowClick(post: Post) {
    navigate(`/board/${post.id}`);
  }

  function handleCloseWrite(success?: boolean) {
    setShowWrite(false);
    if (success) {
      setTimeout(() => loadPosts("Free-Talk"), 120);
    }
  }

  // ⭐ 새로운 페이지네이션 범위 계산
  const startPage = Math.max(1, page - 2);
  const endPage = Math.min(totalPages, page + 2);

  return (
    <div className="max-w-4xl mx-auto mt-8">
      {/* 상단 영역 */}
      <div className="mb-4 flex justify-between items-center">
        <h2 className="text-xl font-bold">Free Talk</h2>
        {user ? (
          <button className="px-3 py-1 rounded bg-black text-white" onClick={() => setShowWrite(true)}>
            글쓰기
          </button>
        ) : (
          <div className="text-gray-400 text-sm">로그인 후 글쓰기 가능</div>
        )}
      </div>

      {/* 카테고리 + 검색 */}
      <div className="mb-4 flex gap-2 flex-wrap">
        <select
          value={selectedCategory}
          onChange={e => setSelectedCategory(e.target.value)}
          className="border rounded px-2"
          aria-label="카테고리"
        >
          <option value="all">전체</option>
          {FREETALK_CATEGORIES.map(cat => (
            <option key={cat.code} value={cat.code}>{cat.label}</option>
          ))}
        </select>

        <select
          value={searchType}
          onChange={e => setSearchType(e.target.value as any)}
          className="border rounded px-2"
        >
          <option value="title">제목</option>
          <option value="title+content">제목+내용</option>
          <option value="author">작성자</option>
        </select>
        <input
          className="border rounded px-2 py-1 flex-1 min-w-[200px]"
          placeholder="검색어를 입력하세요"
          value={searchKeyword}
          onChange={e => setSearchKeyword(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") setPage(1); }}
        />
        <button className="px-3 py-1 rounded bg-gray-200" onClick={() => setPage(1)}>검색</button>
      </div>

      {/* 게시글 목록 */}
      <BoardList
        posts={pagedPosts}
        allPosts={filtered}
        page={page}
        pageSize={PAGE_SIZE}
        onRowClick={handleRowClick}
        pinnedNotices={pinnedNotices}
        onNoticeClick={setSelectedNotice}
      />

      {/* 공지 상세 모달 */}
      {selectedNotice && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-20">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-lg w-full relative max-h-[80vh] overflow-y-auto">
            <button
              onClick={() => setSelectedNotice(null)}
              className="absolute top-3 right-3 text-gray-400 hover:text-black z-30"
            >닫기</button>
            <div className="font-bold text-lg mb-2">{selectedNotice.title}</div>
            <div className="text-xs text-gray-500 mb-4">{selectedNotice.created_at?.slice(0,10)}</div>
            <div className="text-gray-800 whitespace-pre-line">{selectedNotice.content}</div>
          </div>
        </div>
      )}

      {/* ⭐⭐⭐ 새로운 미니멀 페이지네이션 (B 스타일 적용) ⭐⭐⭐ */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-10 mb-10 text-sm select-none">

          {/* First */}
          <button
            onClick={() => setPage(1)}
            disabled={page === 1}
            className={`px-2 py-1 rounded ${
              page === 1 ? "text-gray-300" : "hover:bg-gray-100"
            }`}
          >
            «
          </button>

          {/* Prev */}
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className={`px-2 py-1 rounded ${
              page === 1 ? "text-gray-300" : "hover:bg-gray-100"
            }`}
          >
            ‹
          </button>

          {/* Short range page numbers */}
          {Array.from(
            { length: Math.max(0, endPage - startPage + 1) },
            (_, i) => startPage + i
          ).map(num => (
            <button
              key={num}
              onClick={() => setPage(num)}
              className={`px-3 py-1 rounded ${
                page === num
                  ? "bg-black text-white"
                  : "hover:bg-gray-100 text-gray-700"
              }`}
            >
              {num}
            </button>
          ))}

          {/* Next */}
          <button
            onClick={() => setPage(page + 1)}
            disabled={page === totalPages}
            className={`px-2 py-1 rounded ${
              page === totalPages ? "text-gray-300" : "hover:bg-gray-100"
            }`}
          >
            ›
          </button>

          {/* Last */}
          <button
            onClick={() => setPage(totalPages)}
            disabled={page === totalPages}
            className={`px-2 py-1 rounded ${
              page === totalPages ? "text-gray-300" : "hover:bg-gray-100"
            }`}
          >
            »
          </button>
        </div>
      )}

      {/* 글쓰기 모달 */}
      {showWrite && (
        <div className="fixed inset-0 bg-black/40 z-30 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl relative max-h-[90vh] overflow-auto">
            <button
              className="absolute right-3 top-3 text-gray-400 hover:text-black"
              onClick={() => handleCloseWrite(false)}
              aria-label="닫기"
            >
              ✕
            </button>
            <div className="p-6">
              <BoardWritePage boardType="Free-Talk" onClose={handleCloseWrite} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
